import { SITE, canonical, ORG_ID, WEBSITE_ID, PERSON_ID } from "./site";
import {
  SHRINKTIONARY_TERM_SLUGS,
  shrinktionaryTerm,
} from "./networkDeepLinks";

export interface Crumb {
  name: string;
  path: string;
}

// The full Organization node is defined once in the head @graph (index.html),
// which ships on every page. Everywhere the Organization is referenced (as a
// publisher, isPartOf target, etc.) we emit a bare @id reference so the graph
// merges into a single canonical node and never forks a conflicting definition.
export function organizationSchema() {
  return { "@id": ORG_ID };
}

export interface LegalWebPageInput {
  title: string;
  description: string;
  path: string;
  reviewedAt?: string;
  publishedAt?: string;
  modifiedAt?: string;
  /** When true, embeds the Refai Person block as `reviewedBy`. */
  withReviewer?: boolean;
}

export function legalWebPageSchema(input: LegalWebPageInput) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: input.title,
    description: input.description,
    url: canonical(input.path),
    inLanguage: "en-US",
    isPartOf: { "@id": WEBSITE_ID },
    publisher: organizationSchema(),
    ...(input.withReviewer ? { reviewedBy: authorPerson() } : {}),
    ...(input.publishedAt ? { datePublished: input.publishedAt } : {}),
    ...(input.modifiedAt ? { dateModified: input.modifiedAt } : {}),
    ...(input.reviewedAt ? { lastReviewed: input.reviewedAt } : {}),
  };
}

export interface MapWebPageInput {
  title: string;
  description: string;
  path: string;
  datePublished: string;
  dateModified: string;
}

// WebPage entity for an Anxiety Map page (hub or individual map). Carries the
// shared Refai Person as both author and reviewer, and nests the
// AnxietyResource Organization with `parentOrganization` set to The Shrink
// Network, so the maps merge into the same entity graph as the rest of the site.
export function mapWebPageSchema(input: MapWebPageInput) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: input.title,
    description: input.description,
    url: canonical(input.path),
    inLanguage: "en-US",
    datePublished: input.datePublished,
    dateModified: input.dateModified,
    author: authorPerson(),
    reviewedBy: authorPerson(),
    isPartOf: { "@id": ORG_ID },
  };
}

// §7 WebPage entity for the Start Here entry point. Carries the shared Refai
// Person as author and reviewer, the AnxietyResource Organization as publisher,
// and is part of both the WebSite and the AnxietyResource Organization (whose
// parent is The Shrink Network), merging into the same entity graph.
export function startHereWebPageSchema(input: { path: string }) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Start Here",
    description:
      "AnxietyResource's first-time-visitor entry point, what brought you here and where to start.",
    url: canonical(input.path),
    inLanguage: "en-US",
    author: authorPerson(),
    reviewedBy: authorPerson(),
    publisher: organizationSchema(),
    isPartOf: [{ "@id": WEBSITE_ID }, { "@id": ORG_ID }],
  };
}

// The full WebSite node (with SearchAction) is defined once in the head @graph
// (index.html). References emit a bare @id so the graph merges to one node.
export function websiteSchema() {
  return { "@id": WEBSITE_ID };
}

export function breadcrumbSchema(crumbs: Crumb[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: canonical(c.path),
    })),
  };
}

export interface ArticleSchemaInput {
  title: string;
  description: string;
  path: string;
  image?: string;
  publishedAt: string;
  modifiedAt: string;
  reviewedAt: string;
  category: string;
  /** Reading time in minutes, emitted as ISO 8601 `timeRequired`. */
  readingTime?: number;
  keywords?: string[];
}

function absoluteImage(image?: string) {
  if (!image) return SITE.ogImage;
  if (image.startsWith("http")) return image;
  return `${SITE.url}${image.startsWith("/") ? image : `/${image}`}`;
}

// The canonical Refai Person node is defined once in the head @graph
// (index.html) and is byte-identical across every Shrink Network property so
// search engines and AI systems unify the reviewer identity across all sites.
// Everywhere the reviewer is referenced (author, reviewedBy, founder) we emit a
// bare @id reference so the graph merges into that single canonical node.
function authorPerson() {
  return { "@id": PERSON_ID };
}

export function medicalArticleSchema(a: ArticleSchemaInput) {
  const url = canonical(a.path);
  const image = [absoluteImage(a.image)];
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "MedicalWebPage",
        "@id": `${url}#webpage`,
        url,
        name: a.title,
        description: a.description,
        inLanguage: "en-US",
        isAccessibleForFree: true,
        lastReviewed: a.reviewedAt,
        reviewedBy: authorPerson(),
        about: { "@type": "MedicalCondition", name: "Anxiety disorders" },
        isPartOf: { "@id": WEBSITE_ID },
        primaryImageOfPage: { "@id": `${url}#primaryimage` },
        speakable: {
          "@type": "SpeakableSpecification",
          cssSelector: [
            ".headline",
            ".quick-answer",
            ".faq-question",
            ".faq-answer",
          ],
        },
      },
      {
        "@type": "ImageObject",
        "@id": `${url}#primaryimage`,
        url: image[0],
        contentUrl: image[0],
      },
      {
        "@type": "Article",
        "@id": `${url}#article`,
        headline: a.title,
        description: a.description,
        datePublished: a.publishedAt,
        dateModified: a.modifiedAt,
        inLanguage: "en-US",
        image,
        author: authorPerson(),
        reviewedBy: authorPerson(),
        publisher: organizationSchema(),
        mainEntityOfPage: { "@id": `${url}#webpage` },
        articleSection: a.category,
        ...(a.readingTime ? { timeRequired: `PT${a.readingTime}M` } : {}),
        ...(a.keywords && a.keywords.length
          ? { keywords: a.keywords.join(", ") }
          : {}),
        speakable: {
          "@type": "SpeakableSpecification",
          cssSelector: [
            ".headline",
            ".quick-answer",
            ".faq-question",
            ".faq-answer",
          ],
        },
      },
    ],
  };
}

export interface MedicalConditionSchemaInput {
  name: string;
  alternateNames?: string[];
  url: string;
  description: string;
  signOrSymptom?: string[];
  associatedAnatomy?: string;
}

export function medicalConditionSchema(condition: MedicalConditionSchemaInput) {
  const out: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "MedicalCondition",
    name: condition.name,
    url: condition.url,
    description: condition.description,
    code: { "@type": "MedicalCode", codingSystem: "DSM-5-TR" },
  };
  if (condition.alternateNames && condition.alternateNames.length) {
    out.alternateName = condition.alternateNames;
  }
  if (condition.signOrSymptom && condition.signOrSymptom.length) {
    out.signOrSymptom = condition.signOrSymptom.map((s) => ({
      "@type": "MedicalSignOrSymptom",
      name: s,
    }));
  }
  if (condition.associatedAnatomy) {
    out.associatedAnatomy = {
      "@type": "AnatomicalStructure",
      name: condition.associatedAnatomy,
    };
  }
  return out;
}

export function itemListSchema(
  items: { name: string; url: string; description?: string }[],
  listName?: string,
) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    ...(listName ? { name: listName } : {}),
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: item.url,
      name: item.name,
      ...(item.description ? { description: item.description } : {}),
    })),
  };
}

export interface MedicalScholarlyArticleInput {
  title: string;
  description: string;
  path: string;
  publishedAt: string;
  modifiedAt: string;
  reviewedAt: string;
  conditionName?: string;
}

export function medicalScholarlyArticleSchema(
  a: MedicalScholarlyArticleInput,
) {
  const url = canonical(a.path);
  return {
    "@context": "https://schema.org",
    "@type": "MedicalScholarlyArticle",
    headline: a.title,
    description: a.description,
    datePublished: a.publishedAt,
    dateModified: a.modifiedAt,
    lastReviewed: a.reviewedAt,
    author: authorPerson(),
    reviewedBy: authorPerson(),
    publisher: organizationSchema(),
    mainEntityOfPage: { "@id": `${url}#webpage` },
    audience: { "@type": "MedicalAudience", audienceType: "Patient" },
    specialty: "Psychiatry",
    ...(a.conditionName
      ? { about: { "@type": "MedicalCondition", name: a.conditionName } }
      : {}),
  };
}

export interface HowToStep {
  name: string;
  text: string;
  url?: string;
}

export interface HowToInput {
  name: string;
  description: string;
  steps: HowToStep[];
  totalTime?: string;
}

export function howToSchema(input: HowToInput) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: input.name,
    description: input.description,
    ...(input.totalTime ? { totalTime: input.totalTime } : {}),
    step: input.steps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.name,
      text: s.text,
      ...(s.url ? { url: s.url } : {}),
    })),
  };
}

export function faqSchema(faq: { q: string; a: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function shrinkmdOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "MedicalBusiness",
    name: "shrinkMD",
    url: "https://shrinkmd.com",
    founder: authorPerson(),
    medicalSpecialty: "Psychiatry",
    serviceType: "Telepsychiatry",
  };
}

export interface DefinedTermInput {
  term: string;
  slug: string;
  shortDef: string;
}

const GLOSSARY_URL = `${SITE.url}/glossary`;

/**
 * Cross-network `sameAs` target for a glossary term. When the exact slug
 * exists as a Shrinktionary /terms/ page, the DefinedTerm node emits a
 * `sameAs` pointing at the canonical language-layer entry. Search engines
 * and AI systems use `sameAs` to unify the two entries as one concept
 * across the network, which is exactly what we want for terminology that
 * is defined on both sites.
 */
function termSameAs(slug: string): string | undefined {
  return SHRINKTIONARY_TERM_SLUGS.has(slug)
    ? shrinktionaryTerm(slug)
    : undefined;
}

export function definedTermSetSchema(terms: DefinedTermInput[]) {
  return {
    "@context": "https://schema.org",
    "@type": "DefinedTermSet",
    name: "AnxietyResource Glossary",
    description:
      "Plain-language definitions of anxiety, panic, and nervous system terminology.",
    url: GLOSSARY_URL,
    hasDefinedTerm: terms.map((t) => {
      const sameAs = termSameAs(t.slug);
      return {
        "@type": "DefinedTerm",
        name: t.term,
        description: t.shortDef,
        url: `${GLOSSARY_URL}/${t.slug}`,
        inDefinedTermSet: GLOSSARY_URL,
        ...(sameAs ? { sameAs } : {}),
      };
    }),
  };
}

export function definedTermSchema(term: DefinedTermInput) {
  const sameAs = termSameAs(term.slug);
  return {
    "@context": "https://schema.org",
    "@type": "DefinedTerm",
    name: term.term,
    description: term.shortDef,
    url: `${GLOSSARY_URL}/${term.slug}`,
    inDefinedTermSet: {
      "@type": "DefinedTermSet",
      name: "AnxietyResource Glossary",
      url: GLOSSARY_URL,
    },
    ...(sameAs ? { sameAs } : {}),
  };
}

export interface GlossaryEntrySchemaInput {
  term: string;
  shortDef: string;
  slug: string;
  publishedAt: string;
  updatedAt: string;
  reviewedAt: string;
}

export function medicalGlossaryEntrySchema(e: GlossaryEntrySchemaInput) {
  const url = canonical(`/glossary/${e.slug}`);
  const sameAs = termSameAs(e.slug);
  return {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    "@id": `${url}#webpage`,
    url,
    name: `${e.term}: plain-language definition`,
    description: e.shortDef,
    inLanguage: "en-US",
    isAccessibleForFree: true,
    datePublished: e.publishedAt,
    dateModified: e.updatedAt,
    lastReviewed: e.reviewedAt,
    reviewedBy: authorPerson(),
    about: { "@type": "MedicalCondition", name: "Anxiety disorders" },
    isPartOf: {
      "@type": "DefinedTermSet",
      name: "AnxietyResource Glossary",
      url: GLOSSARY_URL,
    },
    publisher: organizationSchema(),
    ...(sameAs ? { sameAs } : {}),
  };
}

export interface BookSchemaInput {
  title: string;
  subtitle?: string;
  url: string;
  externalUrl: string;
  description: string;
  cover?: string;
  isPartOfSeriesUrl?: string;
  isRelatedToUrl?: string;
  /** ISBN-13 (with hyphens). Emitted as `isbn` on the Book node. */
  isbn?: string;
  /** Maps to schema.org `bookFormat` (e.g. "Paperback" -> https://schema.org/Paperback). */
  bookFormat?: "Paperback" | "Hardcover" | "EBook" | "AudiobookFormat";
  /** Overrides the default publisher name (`shrinkMD Publishing`) with the
   *  legal entity tied to the ISBN (e.g. `shrinkMD Publishing LLC`). */
  publisherLegalName?: string;
}

export function bookSchema(book: BookSchemaInput) {
  const base: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Book",
    name: book.title,
    url: book.url,
    sameAs: book.externalUrl,
    ...(book.cover
      ? {
          image: book.cover.startsWith("http")
            ? book.cover
            : `${SITE.url}${book.cover}`,
        }
      : {}),
    description: book.description,
    inLanguage: "en",
    datePublished: "2026",
    author: authorPerson(),
    publisher: {
      "@type": "Organization",
      name: book.publisherLegalName ?? "shrinkMD Publishing",
    },
    ...(book.isbn ? { isbn: book.isbn } : {}),
    ...(book.bookFormat
      ? { bookFormat: `https://schema.org/${book.bookFormat}` }
      : {}),
  };
  if (book.subtitle) base.alternativeHeadline = book.subtitle;
  if (book.isPartOfSeriesUrl) {
    base.isPartOf = {
      "@type": "BookSeries",
      url: book.isPartOfSeriesUrl,
      name: "Your Mind Is Full of Sh*t series",
    };
  }
  if (book.isRelatedToUrl) {
    base.isRelatedTo = {
      "@type": "Book",
      url: book.isRelatedToUrl,
    };
  }
  return base;
}

export function booksListSchema(
  books: {
    title: string;
    externalUrl: string;
    shortDescription: string;
    isbn?: string;
    bookFormat?: "Paperback" | "Hardcover" | "EBook" | "AudiobookFormat";
    publisherLegalName?: string;
  }[],
) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: books.map((b, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "Book",
        name: b.title,
        author: authorPerson(),
        publisher: {
          "@type": "Organization",
          name: b.publisherLegalName ?? "shrinkMD Publishing",
        },
        datePublished: "2026",
        inLanguage: "en",
        url: b.externalUrl,
        about: "Anxiety, overthinking, panic, and the nervous system",
        description: b.shortDescription,
        ...(b.isbn ? { isbn: b.isbn } : {}),
        ...(b.bookFormat
          ? { bookFormat: `https://schema.org/${b.bookFormat}` }
          : {}),
      },
    })),
  };
}

export function collectionPageSchema(input: {
  title: string;
  description: string;
  path: string;
  mainEntityUrl?: string;
  /** Last editorial review date, ISO `YYYY-MM-DD`. Maps to schema.org `lastReviewed`. */
  reviewedAt?: string;
  /** First publication date, ISO `YYYY-MM-DD`. */
  publishedAt?: string;
  /** Last modification date, ISO `YYYY-MM-DD`. */
  modifiedAt?: string;
  /** When true, embeds the Refai Person block as `reviewedBy`. */
  withReviewer?: boolean;
  /** Government/organization sources cited by the page, emitted as `citation`. */
  citations?: { name: string; url: string }[];
}) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: input.title,
    description: input.description,
    url: canonical(input.path),
    inLanguage: "en-US",
    isPartOf: { "@id": WEBSITE_ID },
    ...(input.publishedAt ? { datePublished: input.publishedAt } : {}),
    ...(input.modifiedAt ? { dateModified: input.modifiedAt } : {}),
    ...(input.reviewedAt ? { lastReviewed: input.reviewedAt } : {}),
    ...(input.withReviewer ? { reviewedBy: authorPerson() } : {}),
    ...(input.citations?.length
      ? {
          citation: input.citations.map((c) => ({
            "@type": "WebPage",
            name: c.name,
            url: c.url,
          })),
        }
      : {}),
    ...(input.mainEntityUrl
      ? { mainEntity: { "@type": "ItemList", url: input.mainEntityUrl } }
      : {}),
  };
}

export function resourcesItemListSchema(
  items: { name: string; url: string; description: string }[],
  listName = "Mental health and anxiety resources"
) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: listName,
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "Organization",
        name: item.name,
        url: item.url,
        description: item.description,
      },
    })),
  };
}

export function webPageItemListSchema(
  items: { name: string; url: string; description: string }[],
  listName = "Pages",
) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: listName,
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: item.url,
      item: {
        "@type": "WebPage",
        "@id": item.url,
        url: item.url,
        name: item.name,
        description: item.description,
      },
    })),
  };
}

export function digitalDocumentListSchema(
  docs: { name: string; description: string; url: string }[],
  listName = "Helpful downloads"
) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: listName,
    itemListElement: docs.map((d, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "DigitalDocument",
        name: d.name,
        description: d.description,
        encodingFormat: "application/pdf",
        url: d.url,
      },
    })),
  };
}

/**
 * Landing page for a printable tool (/tools/<slug>). The page is a
 * MedicalWebPage whose main entity is the free downloadable PDF.
 */
export function toolWebPageSchema(input: {
  title: string;
  description: string;
  path: string;
  publishedAt: string;
  updatedAt: string;
  reviewedAt: string;
  pdfUrl: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    name: input.title,
    description: input.description,
    url: canonical(input.path),
    inLanguage: "en-US",
    isPartOf: { "@id": WEBSITE_ID },
    datePublished: input.publishedAt,
    dateModified: input.updatedAt,
    lastReviewed: input.reviewedAt,
    reviewedBy: authorPerson(),
    audience: { "@type": "MedicalAudience", audienceType: "Patient" },
    specialty: "Psychiatry",
    publisher: { "@id": ORG_ID },
    mainEntity: {
      "@type": "DigitalDocument",
      name: input.title,
      description: input.description,
      encodingFormat: "application/pdf",
      url: input.pdfUrl,
      isAccessibleForFree: true,
      author: authorPerson(),
    },
  };
}

export function homeMedicalWebPageSchema(lastReviewedIso: string) {
  return {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    name: "AnxietyResource: anxiety education, symptoms and treatment",
    description:
      "Understand anxiety, panic attacks, overthinking, and related conditions with evidence-based guides, resources, and practical strategies.",
    url: canonical("/"),
    inLanguage: "en-US",
    isPartOf: { "@id": WEBSITE_ID },
    about: {
      "@type": "MedicalCondition",
      name: "Anxiety disorders",
      alternateName: ["Anxiety", "Anxiety disorder"],
    },
    audience: { "@type": "MedicalAudience", audienceType: "Patient" },
    specialty: "Psychiatry",
    reviewedBy: personSchema(),
    lastReviewed: lastReviewedIso,
    publisher: { "@id": ORG_ID },
    significantLink: [
      canonical("/symptoms"),
      canonical("/panic-nervous-system"),
      canonical("/overthinking-rumination"),
      canonical("/conditions"),
      canonical("/treatment-education"),
      canonical("/glossary"),
    ],
  };
}

// The full Person node lives once in the head @graph (index.html), byte-identical
// across the network. Pages that describe or are reviewed by the reviewer emit a
// bare @id reference that merges into that canonical node.
export function personSchema() {
  return { "@id": PERSON_ID };
}

export type HowToStepInput = HowToStep;
